import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Activity } from 'lucide-react';
import { auditListQueryOptions } from '@/lib/audit';

interface Props {
  postId: string;
}

const HUMAN_ACTION: Record<string, string> = {
  'post.create': 'สร้าง',
  'post.update': 'แก้ไข',
  'post.publish': 'เผยแพร่',
  'post.unpublish': 'ถอนเผยแพร่',
  'post.delete': 'ย้ายเข้าถังขยะ',
  'post.restore': 'กู้คืนจากถังขยะ',
  'post.purge': 'ลบถาวร',
  'post.duplicate': 'ทำสำเนา',
};

function formatRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.round(diffMs / 60_000);
  if (min < 1) return 'เมื่อสักครู่';
  if (min < 60) return `${min} นาทีที่แล้ว`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} ชั่วโมงที่แล้ว`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day} วันที่แล้ว`;
  return new Date(iso).toLocaleDateString('th-TH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Per-post activity panel. Lists the most recent audit log entries
 * scoped to this post id (resourceType='post' + resourceId=$postId).
 * Permission-gated server-side on audit.read — non-readers get 403
 * which we silently swallow into an empty panel (no error noise on
 * the editor for roles that just don't have audit access).
 */
export function PostAuditPanel({ postId }: Props) {
  const query = useQuery({
    ...auditListQueryOptions({
      resourceType: 'post',
      resourceId: postId,
      perPage: 10,
    }),
    // Audit feed lags by seconds; a 30s stale window avoids re-fetching
    // on every focus change of the editor without losing the "I just
    // saved, did it record?" signal.
    staleTime: 30_000,
    // Don't blow up the editor if the user lacks audit.read.
    retry: false,
  });

  if (query.isError) return null;
  const items = query.data?.items ?? [];

  return (
    <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 dark:border-slate-800 dark:text-slate-200">
        <Activity className="h-4 w-4 text-slate-400" />
        ประวัติการแก้ไขบทความนี้
        {/* Drill-through to /audit pre-filtered by this post — useful
            when the inline panel's 10-row cap hides older edits or
            the user wants the full audit chrome (date range, export). */}
        <Link
          to="/audit"
          search={{ resourceType: 'post', resourceId: postId }}
          className="ml-auto text-xs font-normal text-blue-600 hover:underline dark:text-blue-400"
        >
          ดูทั้งหมด →
        </Link>
      </div>
      <div className="p-3">
        {query.isLoading ? (
          <p className="text-xs text-slate-400">กำลังโหลด...</p>
        ) : items.length === 0 ? (
          <p className="text-xs text-slate-400">ยังไม่มีกิจกรรมที่บันทึก</p>
        ) : (
          <ul className="space-y-1.5">
            {items.map((a) => (
              <li key={a.id} className="flex items-baseline gap-2 text-xs">
                <span className="shrink-0 font-medium text-slate-700 dark:text-slate-200">
                  {HUMAN_ACTION[a.action] ?? a.action}
                </span>
                <span className="text-slate-500">โดย {a.userName ?? '—'}</span>
                <span className="ml-auto shrink-0 text-slate-400">
                  {formatRelative(a.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
